import React from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from '@fuse/hooks';

import { getDefaultFilters } from 'app/main/configs';
import ModuleOptionsListItem from 'app/main/modules/components/ModuleOptionsListItem';

import { type MiddlewhereFilter } from '../../filter/FilterItemManager';
import { type FieldProps } from '../components/BaseTextField';
import { delayPromise, executeStored, formatSearchString } from '../../Utils';
import useSettings from '../../hooks/useSettings';
import useUserData from '../../hooks/useUserData';

import { ModuleSearchPaperComponent } from './ModuleSearchComponents';

export interface ModuleSearchOptionsListItemProps {
	settings: any;
	option: any;
}

export type UseModuleSearchProps = {
	/** The options module */
	module: string;
	/** The options module id field */
	idField?: string;
	/** The selected item uid */
	uid?: string | null;
	/** A list of filters applied on the options list */
	filters?: MiddlewhereFilter[];
	/** Function used to determine the string label for a given option */
	getOptionLabel?: (option: any) => string;
	/** Function used to send changes to the model */
	sendChange?: (newValue: any) => void;
} & Pick<FieldProps<any>, 'value' | 'setValue'>;

export default function useModuleSearch({ uid, value, setValue, ...props }: UseModuleSearchProps) {
	const dispatch = useDispatch();
	const { settings } = useSettings(props.module);
	const userData = useUserData();

	const idField = props.idField || `${settings.modu_short}_uid`;

	const defaultFilters = getDefaultFilters({ settings, defaultFilters: props.filters || [], userData });

	const [inputValue, setInputValue] = React.useState('');

	const [options, setOptions] = React.useState<any[]>([]);
	const [totalOptionsCount, setTotalOptionsCount] = React.useState<number | undefined>();

	const [isLoading, setIsLoading] = React.useState(false);
	const [previousUid, setPreviousUid] = React.useState<string | undefined>();

	const isOptionEqualToValue = React.useCallback(
		(option, currentValue) => option[idField] === currentValue[idField],
		[idField]
	);

	const getTitle = React.useCallback(option => option?.[`${settings.modu_short}_title`] || '', [settings.modu_short]);

	const parseOptionLabel = option =>
		option && props.getOptionLabel ? props.getOptionLabel(option) : getTitle(option);

	const fetchOptions = React.useCallback(
		(moduleSettings: any, filters: MiddlewhereFilter[]) => {
			const action = `${moduleSettings.modu_external ? 'ext_' : ''}st_${moduleSettings.modu_title}_list`;
			const params = { n_page: 1, n_rows: 10, filters };
			return executeStored({ action, dispatch, params });
		},
		[dispatch]
	);

	const delayedFetchOptions = (moduleSettings: any, filters: MiddlewhereFilter[]) => {
		setIsLoading(true);
		delayPromise(fetchOptions(moduleSettings, filters)).then(response => {
			setTotalOptionsCount(response.data.n_record);
			setOptions(response.data.list || []);
			setIsLoading(false);
		});
	};

	const debouncedFetchOptions = React.useRef(debounce(delayedFetchOptions, 500)).current;

	function handleInputChange(_event: React.SyntheticEvent, newInputValue: string, reason: string) {
		debouncedFetchOptions.cancel();

		setInputValue(newInputValue);
		if (reason !== 'createOption') {
			setTotalOptionsCount(0);
			setOptions([]);
		}

		if (['input', 'blur'].includes(reason)) {
			const searchString = formatSearchString(newInputValue);
			if (!!searchString && searchString !== '' && newInputValue?.toLowerCase() !== inputValue?.toLowerCase()) {
				debouncedFetchOptions(settings, [
					...defaultFilters,
					{ filt_field: `${settings.modu_short}_deleted`, filt_clause: 'not_equal', filt_value: 1 },
					{ filt_field: `${settings.modu_short}_search`, filt_clause: 'like', filt_value: searchString }
				]);
			}
		}
	}

	function handleChange(newValue: any) {
		if (newValue) {
			setValue(newValue);
			props.sendChange?.(newValue[idField]);
		} else {
			setValue(null);
			props.sendChange?.(null);
		}
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
		if (event.key === 'Enter') {
			// event.preventDefault();
			event.stopPropagation();
		}
	}

	function renderOption({ key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: any) {
		return (
			<ModuleOptionsListItem
				key={key}
				settings={settings}
				option={option}
				optionProps={optionProps}
				fallbackLabel={parseOptionLabel(option)}
			/>
		);
	}

	useDeepCompareEffect(() => {
		if (uid && uid !== value?.[idField] && uid !== previousUid) {
			const filters = [{ filt_field: idField, filt_clause: 'equals', filt_value: uid }];
			fetchOptions(settings, filters).then(response => {
				setValue(response.data.list?.[0]);
				setPreviousUid(uid);
			});
		} else if (!uid && value) {
			setValue(undefined);
			setPreviousUid(undefined);
		}
	}, [fetchOptions, idField, setValue, uid, value]);

	const PaperComponent = params => ModuleSearchPaperComponent({ options, totalOptionsCount, isLoading, ...params });

	return {
		options,
		isLoading,
		PaperComponent,
		isOptionEqualToValue,
		parseOptionLabel,
		renderOption,
		handleInputChange,
		handleChange,
		handleKeyDown
	};
}
